import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RefreshCw, Copy, ExternalLink, Check, Plus } from 'lucide-react';
import { Button } from './button';
import { toast } from 'sonner';
import {
  GoldenSpiralIcon,
  MandalaIcon,
  SacredFounderIcon,
  AncientSunIcon,
  LotusIcon,
  MetatronIcon,
  CompassIcon,
  SoulSignalIcon
} from './legendary-icons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Score Gauge Component with organic design
const ScoreGauge = ({ score, label, size = 'md' }) => {
  const getColor = (s) => {
    if (s >= 80) return '#10B981';
    if (s >= 60) return '#F59E0B';
    if (s >= 40) return '#EF4444';
    return '#6B7280';
  };
  
  const circumference = 2 * Math.PI * 40;
  const progress = (score / 100) * circumference;
  
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };
  
  return (
    <div className={`relative ${sizeClasses[size]} flex items-center justify-center`}>
      <svg className="transform -rotate-90 w-full h-full">
        <circle
          cx="50%"
          cy="50%"
          r="40%"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-white/10"
        />
        <circle
          cx="50%"
          cy="50%"
          r="40%"
          stroke={getColor(score)}
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: getColor(score) }}>{Math.round(score)}</span>
        {label && <span className="text-[10px] text-white/50 uppercase tracking-wide">{label}</span>}
      </div>
    </div>
  );
};

// Grade Badge with sacred geometry
const GradeBadge = ({ grade }) => {
  const gradeColors = {
    'A': 'from-emerald-500 to-emerald-600',
    'B': 'from-blue-500 to-blue-600',
    'C': 'from-amber-500 to-amber-600',
    'D': 'from-orange-500 to-orange-600',
    'F': 'from-red-500 to-red-600'
  };
  
  return (
    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradeColors[grade] || gradeColors['C']} flex items-center justify-center shadow-lg relative overflow-hidden`}>
      <div className="absolute inset-0 opacity-20">
        <MandalaIcon size={56} className="text-white" />
      </div>
      <span className="text-2xl font-bold text-white relative z-10">{grade}</span>
    </div>
  );
};

// Beautiful "Add to Tasks" Button Component
const AddToTasksButton = ({ onClick, label = "Add to Tasks" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  
  const handleClick = () => {
    setIsAdded(true);
    onClick();
    setTimeout(() => setIsAdded(false), 2000);
  };
  
  return (
    <motion.button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`
        relative overflow-hidden px-4 py-2.5 rounded-xl
        border transition-all duration-300
        ${isAdded 
          ? 'bg-emerald-500/20 border-emerald-500/50' 
          : 'bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-amber-500/10 border-white/20 hover:border-indigo-400/50'
        }
      `}
      data-testid="add-to-tasks-btn"
    >
      {/* Animated background gradient */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-amber-500/20"
        animate={{
          x: isHovered ? ['-100%', '100%'] : '-100%',
        }}
        transition={{ duration: 1, ease: 'easeInOut' }}
      />
      
      {/* Sacred geometry background */}
      <div className="absolute inset-0 opacity-10 flex items-center justify-center">
        <GoldenSpiralIcon size={40} className="text-white" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 flex items-center gap-2">
        {isAdded ? (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"
            >
              <Check size={12} className="text-white" />
            </motion.div>
            <span className="text-sm font-medium text-emerald-300">Added!</span>
          </>
        ) : (
          <>
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <Plus size={12} className="text-white" />
            </div>
            <span className="text-sm font-medium text-white/80">{label}</span>
          </>
        )}
      </div>
      
      {/* Sparkle effect on hover */}
      {isHovered && !isAdded && (
        <motion.div
          className="absolute top-1 right-1"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <AncientSunIcon size={12} className="text-amber-400" />
        </motion.div>
      )}
    </motion.button>
  );
};

// Main Brand Checker Component
export const BrandChecker = ({ onClose, onAddToTasks, startupResponses = {} }) => {
  const [brandName, setBrandName] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [generatedNames, setGeneratedNames] = useState([]);
  const [description, setDescription] = useState('');
  const [activeTab, setActiveTab] = useState('check');
  const [useExistingInputs, setUseExistingInputs] = useState(false);
  
  // Build context from startup responses for name generation
  const buildStartupContext = () => {
    const contextParts = [];
    
    // Pull key information from startup responses
    if (startupResponses.north_origin_story) {
      contextParts.push(`Origin: ${startupResponses.north_origin_story}`);
    }
    if (startupResponses.north_core_values) {
      contextParts.push(`Values: ${startupResponses.north_core_values}`);
    }
    if (startupResponses.north_purpose_statement) {
      contextParts.push(`Purpose: ${startupResponses.north_purpose_statement}`);
    }
    if (startupResponses.east_hero_customer) {
      contextParts.push(`Customer: ${startupResponses.east_hero_customer}`);
    }
    if (startupResponses.east_problem_statement) {
      contextParts.push(`Problem: ${startupResponses.east_problem_statement}`);
    }
    if (startupResponses.east_solution_promise) {
      contextParts.push(`Solution: ${startupResponses.east_solution_promise}`);
    }
    if (startupResponses.east_brand_voice) {
      contextParts.push(`Brand Voice: ${startupResponses.east_brand_voice}`);
    }
    if (startupResponses.southeast_market_size) {
      contextParts.push(`Market: ${startupResponses.southeast_market_size}`);
    }
    if (startupResponses.southeast_positioning) {
      contextParts.push(`Positioning: ${startupResponses.southeast_positioning}`);
    }
    
    return contextParts.join('\n\n');
  };
  
  const hasExistingInputs = Object.keys(startupResponses).length > 0 && 
    Object.values(startupResponses).some(v => v && v.trim());
  
  const checkBrandName = async () => {
    if (!brandName.trim()) {
      toast.error('Please enter a brand name');
      return;
    }
    
    setIsChecking(true);
    setResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/brand/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: brandName,
          check_domain: true,
          check_trademark: true,
          generate_alternatives: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        toast.error('Failed to check brand name');
      }
    } catch (error) {
      console.error('Brand check error:', error);
      toast.error('Error checking brand name');
    } finally {
      setIsChecking(false);
    }
  };
  
  const generateNames = async () => {
    const contextToUse = useExistingInputs ? buildStartupContext() : description;
    
    if (!contextToUse.trim()) {
      toast.error(useExistingInputs 
        ? 'No startup inputs found. Please fill in some sections first.' 
        : 'Please describe your business'
      );
      return;
    }
    
    setIsGenerating(true);
    setGeneratedNames([]);
    
    try {
      const response = await fetch(`${API_URL}/api/brand/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: contextToUse,
          keywords: [],
          style: 'modern',
          use_full_context: useExistingInputs
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedNames(data.suggestions || []);
        if (data.suggestions?.length > 0) {
          toast.success(`Generated ${data.suggestions.length} brand name suggestions!`);
        }
      } else {
        toast.error('Failed to generate names');
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error('Error generating names');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleAddTask = (taskText) => {
    if (onAddToTasks) {
      onAddToTasks({
        title: taskText,
        category: 'brand',
        priority: 'medium'
      });
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-slate-900 rounded-2xl border border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center relative">
                <MetatronIcon size={28} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Brand Name Checker</h2>
                <p className="text-sm text-white/60">Validate & score your brand name</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white/60 hover:text-white">
              <X size={20} />
            </Button>
          </div>
          
          {/* Tabs with custom icons */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('check')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'check' 
                  ? 'bg-white/20 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              }`}
            >
              <CompassIcon size={16} className={activeTab === 'check' ? 'text-indigo-300' : ''} />
              Check Name
            </button>
            <button
              onClick={() => setActiveTab('generate')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'generate' 
                  ? 'bg-white/20 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              }`}
            >
              <GoldenSpiralIcon size={16} className={activeTab === 'generate' ? 'text-purple-300' : ''} />
              Generate Names
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'check' ? (
            <div className="space-y-6">
              {/* Input */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Enter your brand name
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="e.g., Lumina, Bliss Abode, TechFlow..."
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && checkBrandName()}
                    data-testid="brand-name-input"
                  />
                  <Button
                    onClick={checkBrandName}
                    disabled={isChecking || !brandName.trim()}
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-6"
                    data-testid="check-brand-btn"
                  >
                    {isChecking ? (
                      <RefreshCw size={18} className="animate-spin" />
                    ) : (
                      <>
                        <SacredFounderIcon size={18} className="mr-2" />
                        Check
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Results */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  {/* Overall Score Card */}
                  <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <GradeBadge grade={result.grade} />
                        <div>
                          <h3 className="text-2xl font-bold text-white">{result.name}</h3>
                          <p className="text-sm text-white/60">{result.recommendation}</p>
                        </div>
                      </div>
                      <ScoreGauge score={result.overall_score} label="Overall" size="lg" />
                    </div>
                  </div>
                  
                  {/* Score Breakdown */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
                      <ScoreGauge score={result.memorability_score} size="sm" />
                      <p className="text-xs text-white/60 mt-2">Memorability</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
                      <ScoreGauge score={result.uniqueness_score} size="sm" />
                      <p className="text-xs text-white/60 mt-2">Uniqueness</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
                      <ScoreGauge score={result.availability_score} size="sm" />
                      <p className="text-xs text-white/60 mt-2">Availability</p>
                    </div>
                  </div>
                  
                  {/* Domain & Trademark Info */}
                  <div className="grid grid-cols-2 gap-4">
                    {result.domain_info && (
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center gap-2 mb-3">
                          <SoulSignalIcon size={18} className="text-blue-400" />
                          <span className="text-sm font-medium text-white">Domain Check</span>
                          {result.domain_info.available !== undefined && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              result.domain_info.available 
                                ? 'bg-emerald-500/20 text-emerald-300' 
                                : 'bg-red-500/20 text-red-300'
                            }`}>
                              {result.domain_info.available ? 'AVAILABLE' : 'TAKEN'}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white/90 font-mono mb-2">{result.domain_info.domain}</p>
                        <p className="text-xs text-white/50 mb-3">{result.domain_info.message}</p>
                        
                        {/* Domain variations */}
                        {result.domain_info.variations && (
                          <div className="space-y-1 mb-3">
                            {result.domain_info.variations.map((v, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-white/60 font-mono">{v.domain}</span>
                                <span className={v.available ? 'text-emerald-400' : 'text-red-400'}>
                                  {v.available ? '✓' : '✗'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <AddToTasksButton 
                          onClick={() => handleAddTask(`Check domain availability: ${result.domain_info.domain}`)}
                          label="Add Domain Check"
                        />
                      </div>
                    )}
                    
                    {result.trademark_info && (
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center gap-2 mb-3">
                          <LotusIcon size={18} className={
                            result.trademark_info.risk === 'high' ? 'text-red-400' :
                            result.trademark_info.risk === 'medium' ? 'text-amber-400' : 'text-emerald-400'
                          } />
                          <span className="text-sm font-medium text-white">Trademark Risk</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            result.trademark_info.risk === 'high' ? 'bg-red-500/20 text-red-300' :
                            result.trademark_info.risk === 'medium' ? 'bg-amber-500/20 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'
                          }`}>
                            {result.trademark_info.risk.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-xs text-white/70 mb-2">{result.trademark_info.message}</p>
                        
                        {/* USPTO Results */}
                        {result.trademark_info.uspto_results && (
                          <div className="bg-black/20 rounded-lg p-3 mb-3">
                            <p className="text-xs text-white/50 mb-2">USPTO Search Results:</p>
                            {result.trademark_info.uspto_results.length > 0 ? (
                              <ul className="space-y-1">
                                {result.trademark_info.uspto_results.slice(0, 3).map((r, i) => (
                                  <li key={i} className="text-xs text-amber-300 flex items-start gap-2">
                                    <span className="text-amber-400 mt-0.5">•</span>
                                    {r.name} ({r.status})
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-emerald-300">No conflicting marks found</p>
                            )}
                          </div>
                        )}
                        
                        <p className="text-xs text-white/50 mb-3">{result.trademark_info.recommendation}</p>
                        
                        <AddToTasksButton 
                          onClick={() => handleAddTask(`Complete USPTO trademark search for "${result.name}"`)}
                          label="Add Trademark Search"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Strengths & Issues */}
                  <div className="grid grid-cols-2 gap-4">
                    {result.strengths?.length > 0 && (
                      <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
                        <h4 className="text-sm font-medium text-emerald-300 mb-3 flex items-center gap-2">
                          <AncientSunIcon size={16} className="text-emerald-400" />
                          Strengths
                        </h4>
                        <ul className="space-y-2">
                          {result.strengths.map((s, i) => (
                            <li key={i} className="text-xs text-emerald-200/70 flex items-start gap-2">
                              <span className="text-emerald-400 mt-0.5">•</span>
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {result.issues?.length > 0 && (
                      <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
                        <h4 className="text-sm font-medium text-amber-300 mb-3 flex items-center gap-2">
                          <MetatronIcon size={16} className="text-amber-400" />
                          Issues to Address
                        </h4>
                        <ul className="space-y-2">
                          {result.issues.map((issue, i) => (
                            <li key={i} className="text-xs text-amber-200/70 flex items-start gap-2">
                              <span className="text-amber-400 mt-0.5">•</span>
                              {issue}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  {/* Alternatives */}
                  {result.alternatives?.length > 0 && (
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <h4 className="text-sm font-medium text-white/70 mb-3 flex items-center gap-2">
                        <GoldenSpiralIcon size={16} className="text-purple-400" />
                        Alternative Suggestions
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {result.alternatives.map((alt, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setBrandName(alt);
                              checkBrandName();
                            }}
                            className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white/70 hover:text-white transition-colors"
                          >
                            {alt}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          ) : (
            /* Generate Tab */
            <div className="space-y-6">
              {/* Use Existing Inputs Toggle */}
              {hasExistingInputs && (
                <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-amber-500/10 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                        <CompassIcon size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Use Your Startup Inputs</p>
                        <p className="text-xs text-white/50">Generate names based on everything you've already written</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setUseExistingInputs(!useExistingInputs)}
                      className={`relative w-14 h-8 rounded-full transition-all ${
                        useExistingInputs 
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-500' 
                          : 'bg-white/20'
                      }`}
                    >
                      <motion.div
                        className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center"
                        animate={{ left: useExistingInputs ? '1.75rem' : '0.25rem' }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      >
                        {useExistingInputs && <Check size={12} className="text-indigo-600" />}
                      </motion.div>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Description Input (only show if not using existing inputs) */}
              {!useExistingInputs && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    Describe your business/product
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g., A sustainable co-living community platform that helps people find like-minded roommates and shared spaces..."
                    className="w-full h-24 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
                    data-testid="brand-description-input"
                  />
                </div>
              )}
              
              {/* Context Preview (when using existing inputs) */}
              {useExistingInputs && hasExistingInputs && (
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <MandalaIcon size={16} className="text-indigo-400" />
                    <span className="text-sm font-medium text-white/70">Using Your Inputs</span>
                  </div>
                  <p className="text-xs text-white/50 line-clamp-4">
                    {buildStartupContext().substring(0, 300)}...
                  </p>
                </div>
              )}
              
              <Button
                onClick={generateNames}
                disabled={isGenerating || (!useExistingInputs && !description.trim())}
                className="w-full bg-gradient-to-r from-purple-500 via-indigo-500 to-amber-500 hover:from-purple-600 hover:via-indigo-600 hover:to-amber-600 text-white py-3"
                data-testid="generate-names-btn"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw size={18} className="mr-2 animate-spin" />
                    Generating Amazing Names...
                  </>
                ) : (
                  <>
                    <LotusIcon size={20} className="mr-2" />
                    Generate Brand Names
                  </>
                )}
              </Button>
              
              {/* Generated Names */}
              {generatedNames.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <h4 className="text-sm font-medium text-white/70 flex items-center gap-2">
                    <AncientSunIcon size={16} className="text-amber-400" />
                    Generated Suggestions
                  </h4>
                  {generatedNames.map((suggestion, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-center justify-between hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold relative overflow-hidden ${
                          suggestion.grade === 'A' ? 'bg-emerald-500/20' :
                          suggestion.grade === 'B' ? 'bg-blue-500/20' :
                          'bg-amber-500/20'
                        }`}>
                          <div className="absolute inset-0 opacity-30">
                            <MandalaIcon size={40} className={
                              suggestion.grade === 'A' ? 'text-emerald-300' :
                              suggestion.grade === 'B' ? 'text-blue-300' :
                              'text-amber-300'
                            } />
                          </div>
                          <span className={`relative z-10 ${
                            suggestion.grade === 'A' ? 'text-emerald-300' :
                            suggestion.grade === 'B' ? 'text-blue-300' :
                            'text-amber-300'
                          }`}>{suggestion.grade}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-white">{suggestion.name}</p>
                          <p className="text-xs text-white/50">{suggestion.reasoning}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-white/70">{Math.round(suggestion.score)}/100</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setBrandName(suggestion.name);
                            setActiveTab('check');
                            setTimeout(checkBrandName, 100);
                          }}
                          className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/20"
                        >
                          <SacredFounderIcon size={14} className="mr-1" />
                          Analyze
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-white/5">
          <div className="flex items-center justify-center gap-2">
            <GoldenSpiralIcon size={14} className="text-white/30" />
            <p className="text-xs text-white/40">
              Powered by Bliss Abode • Will integrate with Vokalize brand search
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BrandChecker;
